// Catalog.ID Client — Device Setup Component
// ============================================================================
// Shared UI for choosing device mode (PIN / open / no storage) and setting up
// the device. Used by the registration wizard and the login view after
// supersecret unlock.

import { createElement } from '@catalog-shell';
import { createPinEntry } from './pin-entry';
import { generateDeviceToken } from '../crypto/credentials';
import * as aes from '../crypto/aes';
import * as accountApi from '../api/account';
import * as deviceApi from '../api/device';
import * as deviceStorage from '../state/device-storage';
import { getDefaultDeviceLabel } from '../state/dashboard-context';
import {
    encryptDeviceLabel, selectAttrEncryptKey,
    MAX_DEVICE_LABEL_CHARS, MAX_DEVICE_LABEL_ENVELOPE_CHARS,
} from '../crypto/device-label';
import { isDeviceStorableKeyId, type KeyBag, type KeyBagEntry } from '../crypto/keybag';
import {
    isDeviceKeyChosen, readDeviceKeyManifest,
    writeDeviceKeyManifest, type DeviceKeyManifest,
} from '../state/device-key-selection';
import {
    createDeviceKeyPicker, type DeviceKeyPicker, type DeviceKeyPickerRow,
} from './device-key-picker';
import {
    describeAuthenticatorError, isCompleteAuthenticatorCode, normalizeAuthenticatorCode,
} from '../auth/authenticator';
import { rememberUnlockFactors } from '../auth/device-unlock';

/**
 * The five choices of device-setup-restructure §6.3: the four device modes plus
 * "no local storage", which registers no device at all. The four are the
 * product of the two orthogonal factors the node stores — `accessmode`
 * (`open` | `pin`) times `totprequired` (§3) — and this type is the client's
 * name for each cell.
 */
export type DeviceSetupMode = 'open' | 'pin' | 'totp' | 'pin+totp' | 'none';

function modeNeedsPin(mode: DeviceSetupMode): boolean {
    return mode === 'pin' || mode === 'pin+totp';
}

function modeNeedsCode(mode: DeviceSetupMode): boolean {
    return mode === 'totp' || mode === 'pin+totp';
}

/** Does this mode keep any keys here at all? "No local storage" does not, so
 *  there is nothing for the picker (§8) to choose between. */
function modeStoresKeys(mode: DeviceSetupMode): boolean {
    return mode !== 'none';
}

export interface DeviceSetupCallbacks {
    /** Called with (deviceToken, deviceEncryptionKey, accessMode, pin?, pinLength?,
     *  totpRequired?, code?, label) after user confirms.
     *
     *  `label` is the PLAINTEXT device name, trimmed and possibly empty. It must
     *  NEVER be forwarded to the server as-is — run it through
     *  {@link encryptLabelForDevice} and send the resulting envelope instead.
     *
     *  `totpRequired` and `code` travel together: the node demands a live code
     *  before it will write a row that needs one (§1 item 5), so every call
     *  site must forward BOTH to `setup` / `pin` / `reset` unchanged. The code
     *  is verified last, immediately before the write, so a request refused for
     *  any other reason leaves it unspent — which is what makes a retry with
     *  the SAME code pointless and a retry with the next one correct.
     *
     *  `manifest` — which keys the member chose to keep on this device (§8) —
     *  is a SECOND ARGUMENT and deliberately not a field of `params`. Every
     *  call site spreads `params` straight into the device request, so a
     *  manifest inside it would travel to the node on the next line, and the
     *  whole point of the selection is that the server never learns it
     *  (§1 item 9, whitepaper §3.4.7). A call site that does not need it simply
     *  declares one parameter; the three wizards take it and remember it beside
     *  the DEK so finalize honours the same choice (§7.3). */
    onSetup: (params: {
        deviceToken: string;
        deviceEncryptionKey: string;
        accessMode: string;
        pin?: string;
        pinLength?: number;
        totpRequired?: boolean;
        code?: string;
        label: string;
    }, manifest: DeviceKeyManifest) => Promise<void>;
    /** Called when the user chooses "no local storage" (no server call needed). */
    onSkip: () => void;
}

/** How a caller frames the screen. */
export interface DeviceSetupOptions {
    /** The commit button's label. Default "Configure device"; a wizard says
     *  what happens next ("Configure device and continue"). */
    confirmLabel?: string;
}

/** Wire fields for a device label — both present, or both absent. */
export interface DeviceLabelFields {
    label?: string;
    labelKeyIndex?: number;
}

/**
 * Encrypt a plaintext device name to the account's newest attr_encrypt public
 * key, ready to send on setup/reset.
 *
 * Returns `{}` — no label — for an empty name AND for every failure. Naming a
 * device is a convenience; device setup is the thing standing between the user
 * and their keys, so a missing public key or a crypto hiccup must never make it
 * fail. Failures are logged, not surfaced.
 */
export async function encryptLabelForDevice(cid: string, label: string): Promise<DeviceLabelFields> {
    const trimmed = label.trim().slice(0, MAX_DEVICE_LABEL_CHARS);
    if (!trimmed) return {};
    try {
        const publicKeys = await accountApi.getPublicKeys(cid);
        const attr = selectAttrEncryptKey(publicKeys);
        if (!attr) {
            console.warn('[device-label] no active attr_encrypt public key — saving device without a name');
            return {};
        }
        const envelope = await encryptDeviceLabel(trimmed, aes.fromBase64(attr.publicKey));
        if (envelope.length > MAX_DEVICE_LABEL_ENVELOPE_CHARS) {
            // Unreachable with a 100-char cap, but a 400 here would fail the
            // whole device setup — drop the name instead.
            console.warn('[device-label] envelope exceeds the server limit — saving device without a name');
            return {};
        }
        return { label: envelope, labelKeyIndex: attr.keyIndex };
    } catch (err) {
        console.warn('[device-label] could not encrypt the device name — saving device without one:', err);
        return {};
    }
}

/**
 * Best-effort rename of the device this browser already owns, for the paths
 * that update an existing device with `changePin` (which carries no label).
 * Silent no-op when we don't know our device id yet.
 */
export async function applyLabelToKnownDevice(cid: string, fields: DeviceLabelFields): Promise<void> {
    if (fields.label === undefined || fields.labelKeyIndex === undefined) return;
    const deviceId = deviceStorage.getDeviceId(cid);
    if (deviceId === null) return;
    try {
        await deviceApi.updateDeviceLabel(deviceId, { label: fields.label, labelKeyIndex: fields.labelKeyIndex });
    } catch (err) {
        console.warn('[device-label] could not update the device name:', err);
    }
}

/**
 * Render the device setup UI into a container.
 * Handles mode selection, PIN entry with confirmation, and localStorage encryption.
 * The caller provides `onSetup` to make the appropriate API call (setup or reset).
 */
export function renderDeviceSetup(
    container: HTMLElement,
    bag: KeyBag,
    cid: string,
    callbacks: DeviceSetupCallbacks,
    setupOptions: DeviceSetupOptions = {},
): void {
    const confirmLabel = setupOptions.confirmLabel ?? 'Configure device';
    // What a "device" is, before anything about one is asked (staging test
    // 2026-10-15: nobody knew). Worded for every caller — the wizards, the
    // device setup after a supersecret sign-in, the Devices pane — and for
    // where it runs: "this computer" first, because inside the dashboard
    // Catalog.ID looks like a desktop app and nobody thinks of a browser. In a
    // plain web browser the keys are kept per browser, which the last sentence
    // says for the people it concerns.
    const deviceExplainer = createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'A device is this computer. Catalog.ID can keep your keys here, encrypted, so that '
            + 'opening your account here takes only your password and the unlock method you choose '
            + 'below. Every other computer you use is set up separately, the first time you sign in '
            + 'there — and so is each web browser, if you also use Catalog.ID in one.',
    });
    container.appendChild(deviceExplainer);

    const introText = createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Choose how keys are stored on this device, and what unlocks them.',
    });

    let selectedMode: DeviceSetupMode = 'pin';
    let selectedPinLength: 4 | 6 | 8 = 4;

    // Device name. Encrypted to the account's own attr_encrypt key before it
    // leaves the browser (crypto/device-label.ts) — the server stores an opaque
    // string, so naming a device never tells it a hostname. Meaningless in
    // "no local storage" mode, which registers no device at all.
    const nameSection = createElement('div', { className: 'cid-device-name-section' });
    const nameLabel = createElement('label', { textContent: 'Device name' });
    nameLabel.setAttribute('for', 'cid-device-name');
    nameLabel.style.display = 'block';
    nameLabel.style.marginBottom = '4px';
    nameSection.appendChild(nameLabel);

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.id = 'cid-device-name';
    nameInput.className = 'cid-auth-input';
    nameInput.style.width = '100%';
    nameInput.maxLength = MAX_DEVICE_LABEL_CHARS;
    nameInput.autocomplete = 'off';
    nameInput.value = getDefaultDeviceLabel();
    nameSection.appendChild(nameInput);

    nameSection.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Only you can read this — it is encrypted with your own key before it is stored.',
    }));
    container.appendChild(nameSection);
    container.appendChild(introText);

    const deviceLabel = (): string => nameInput.value.trim();

    const options = createElement('div', { className: 'cid-device-mode-options' });
    // The four modes in factor order, weakest first, then the choice that
    // stores nothing at all (§6.3).
    const modes: { value: DeviceSetupMode; label: string; desc: string }[] = [
        {
            value: 'open', label: 'No PIN',
            desc: 'Keys stored locally, unlocked automatically on login. For personal devices only.',
        },
        {
            value: 'pin', label: 'PIN',
            desc: 'Keys stored locally, unlocked with a PIN each session. Recommended.',
        },
        {
            value: 'totp', label: 'Authenticator',
            desc: 'Keys stored locally, unlocked with the six-digit code your authenticator app shows.',
        },
        {
            value: 'pin+totp', label: 'PIN + Authenticator',
            desc: 'Keys stored locally, unlocked with your PIN and then a code from your authenticator app.',
        },
        {
            value: 'none', label: 'No local storage',
            desc: 'Enter supersecret each session. Maximum security for shared devices.',
        },
    ];

    const optionEls = new Map<DeviceSetupMode, HTMLElement>();
    for (const mode of modes) {
        const opt = createElement('div', {
            className: `cid-device-mode-option${mode.value === selectedMode ? ' cid-device-mode-option--selected' : ''}`,
        });
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'device-mode';
        radio.value = mode.value;
        radio.checked = mode.value === selectedMode;

        const textCol = createElement('div');
        textCol.appendChild(createElement('div', { className: 'cid-device-mode-label', textContent: mode.label }));
        textCol.appendChild(createElement('div', { className: 'cid-device-mode-desc', textContent: mode.desc }));

        opt.appendChild(radio);
        opt.appendChild(textCol);
        opt.addEventListener('click', () => {
            selectedMode = mode.value;
            radio.checked = true;
            options.querySelectorAll('.cid-device-mode-option').forEach(o =>
                o.classList.toggle('cid-device-mode-option--selected', o === opt));
            pinSection.style.display = modeNeedsPin(mode.value) ? '' : 'none';
            nameSection.style.display = mode.value === 'none' ? 'none' : '';
            // "No local storage" keeps no keys at all, so there is nothing to
            // choose between (§8).
            keysSection.style.display = modeStoresKeys(mode.value) ? '' : 'none';
        });
        options.appendChild(opt);
        optionEls.set(mode.value, opt);
    }
    container.appendChild(options);

    // The Authenticator modes are hidden until the account is known to be able
    // to hold one (§6.3): the key and its replay floor are origin-local, and a
    // member who never had a key issued must not be able to lock this device
    // into a factor they do not hold. Hidden FIRST, revealed on the answer —
    // never the other way round, so a slow or failed check can only ever offer
    // less, never more.
    const authNote = createElement('p', { className: 'cid-pane-note' });
    authNote.style.display = 'none';
    container.appendChild(authNote);
    for (const value of ['totp', 'pin+totp'] as const) optionEls.get(value)!.style.display = 'none';
    void resolveAuthenticatorModes();

    /** Ask the node whether an Authenticator mode may be chosen here, and
     *  either reveal the two options or say in one line why not. Never throws
     *  and never blocks: device setup is the thing standing between the member
     *  and their keys, so it must not fail over an optional mode. */
    async function resolveAuthenticatorModes(): Promise<void> {
        let note: string | null = null;
        try {
            const me = await accountApi.getAccountInfo();
            if (me.totpKeyIssued === false) {
                note = 'This account has no Authenticator Key yet — add one in Settings, under '
                    + 'Authenticator, to unlock this device with a code.';
            }
        } catch (err) {
            console.warn('[device-setup] could not check the Authenticator Key — hiding the code modes:', err);
            note = 'Unlocking with an authenticator code is unavailable right now.';
        }
        if (note) {
            authNote.textContent = note;
            authNote.style.display = '';
            return;
        }
        for (const value of ['totp', 'pin+totp'] as const) optionEls.get(value)!.style.display = '';
    }

    // PIN length selection (visible only for the modes that take a PIN)
    const pinSection = createElement('div');
    if (!modeNeedsPin(selectedMode)) pinSection.style.display = 'none';
    const pinLabel = createElement('p', { className: 'cid-wizard-text', textContent: 'Choose PIN length:' });
    pinSection.appendChild(pinLabel);

    const pinLengthRow = createElement('div', { className: 'cid-auth-pin-length' });
    for (const len of [4, 6, 8] as const) {
        const btn = createElement('button', {
            className: `cs-btn ${len === selectedPinLength ? 'cs-btn--yes' : 'cs-btn--default'} cid-auth-pin-len-btn`,
            textContent: `${len} digits`,
        });
        btn.addEventListener('click', () => {
            selectedPinLength = len;
            pinLengthRow.querySelectorAll('button').forEach(b =>
                b.className = `cs-btn ${b === btn ? 'cs-btn--yes' : 'cs-btn--default'} cid-auth-pin-len-btn`);
        });
        pinLengthRow.appendChild(btn);
    }
    pinSection.appendChild(pinLengthRow);

    const pinEntryArea = createElement('div');
    pinSection.appendChild(pinEntryArea);
    container.appendChild(pinSection);

    // "Keys on this device" (§8, whitepaper §3.4.7). The catalogue as
    // checkboxes under the mode options: mandatory rows ticked and locked,
    // everything else ticked by default (§1 item 14). Rows are shown for key
    // ids the bag does not hold YET — the ceremony mints `xrpl_master` and
    // `derive_root` after this step, and the member may decline one before it
    // exists. Hidden for "No local storage", which stores nothing at all.
    const picker = createDeviceKeyPicker({
        presentKeyIds: (bag.entries as KeyBagEntry[]).map(e => e.keyId),
        manifest: readDeviceKeyManifest(cid),
    });
    const keysSection = renderKeyPickerSection(picker);
    if (!modeStoresKeys(selectedMode)) keysSection.style.display = 'none';
    container.appendChild(keysSection);

    // Confirm button
    const confirmBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: confirmLabel,
    });
    confirmBtn.addEventListener('click', async () => {
        if (selectedMode === 'none') {
            callbacks.onSkip();
            return;
        }
        // Switching away from "no local storage" — clear the flag
        deviceStorage.clearNoLocalStorageMode(cid);
        const mode = selectedMode;
        const label = deviceLabel();
        if (modeNeedsPin(mode)) {
            confirmBtn.style.display = 'none';
            deviceExplainer.style.display = 'none';
            introText.style.display = 'none';
            pinLabel.style.display = 'none';
            pinLengthRow.style.display = 'none';
            options.style.display = 'none';
            authNote.style.display = 'none';
            nameSection.style.display = 'none';
            keysSection.style.display = 'none';
            const pinLen = selectedPinLength;
            renderPinChoice(pinEntryArea, pinLen, pin => { void afterPin(mode, pin, pinLen, label); });
            return;
        }
        await afterPin(mode, undefined, undefined, label);
    });
    container.appendChild(confirmBtn);

    /** The PIN (if any) is settled; ask for a code when the mode needs one,
     *  otherwise write the device. */
    async function afterPin(
        mode: DeviceSetupMode, pin: string | undefined, pinLen: 4 | 6 | 8 | undefined, label: string,
    ): Promise<void> {
        if (modeNeedsCode(mode)) {
            renderCodeStep(mode, pin, pinLen, label);
            return;
        }
        await doSetup(container, bag, cid, {
            accessMode: modeNeedsPin(mode) ? 'pin' : 'open',
            pin, pinLength: pinLen, totpRequired: false, label,
            manifest: picker.toManifest(),
        }, callbacks);
    }

    /**
     * One six-digit field, asked LAST — after the PIN — so the code is as fresh
     * as it can be when the node verifies it (it is checked immediately before
     * the write, §3). A refusal comes straight back here with the next code,
     * because a spent or mistyped code is a thirty-second wait, not a dead end.
     */
    function renderCodeStep(
        mode: DeviceSetupMode, pin: string | undefined, pinLen: 4 | 6 | 8 | undefined,
        label: string, initialError?: string,
    ): void {
        container.innerHTML = '';
        container.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'Type the code your app shows for Catalog.ID',
        }));

        const codeInput = document.createElement('input');
        codeInput.type = 'text';
        codeInput.className = 'cid-auth-input';
        codeInput.placeholder = '000000';
        codeInput.inputMode = 'numeric';
        codeInput.autocomplete = 'one-time-code';
        codeInput.maxLength = 6;
        container.appendChild(codeInput);

        const errorEl = createElement('div', { className: 'cid-pane-error' });
        if (initialError) {
            errorEl.textContent = initialError;
            errorEl.classList.add('cid-pane-error--visible');
        }
        container.appendChild(errorEl);

        const submitBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cid-auth-submit',
            textContent: confirmLabel,
        });
        container.appendChild(submitBtn);

        let busy = false;
        const submit = async (): Promise<void> => {
            if (busy) return;
            const code = normalizeAuthenticatorCode(codeInput.value);
            if (!isCompleteAuthenticatorCode(code)) {
                errorEl.textContent = 'Enter the six digits your authenticator app is showing.';
                errorEl.classList.add('cid-pane-error--visible');
                return;
            }
            busy = true;
            submitBtn.setAttribute('disabled', '');
            codeInput.disabled = true;
            await doSetup(container, bag, cid, {
                accessMode: modeNeedsPin(mode) ? 'pin' : 'open',
                pin, pinLength: pinLen, totpRequired: true, code, label,
                manifest: picker.toManifest(),
            }, callbacks, (_message, err) => {
                // Back to a fresh field with the next code. `doSetup` already
                // emptied the container, so this re-renders the whole step.
                renderCodeStep(mode, pin, pinLen, label, describeAuthenticatorError(err));
            });
        };

        codeInput.addEventListener('input', () => {
            const normalized = normalizeAuthenticatorCode(codeInput.value);
            if (codeInput.value !== normalized) codeInput.value = normalized;
            // Auto-submit the moment the last digit lands — nobody presses a
            // button for a code they just finished typing.
            if (isCompleteAuthenticatorCode(normalized)) void submit();
        });
        codeInput.addEventListener('keydown', (e) => {
            if ((e as KeyboardEvent).key === 'Enter') void submit();
        });
        submitBtn.addEventListener('click', () => { void submit(); });
        codeInput.focus();
    }

    /** Choose a PIN, then type it again. `notice` is shown above a fresh pad —
     *  why it is fresh — until a new first PIN is entered; it must travel as an
     *  argument because this function empties `area` before drawing. */
    function renderPinChoice(
        area: HTMLElement, pinLen: 4 | 6 | 8, onChosen: (pin: string) => void, notice?: string,
    ): void {
        area.innerHTML = '';
        let firstPin = '';
        const noticeEl = notice
            ? createElement('p', { className: 'cid-pane-error cid-pane-error--visible', textContent: notice })
            : null;
        if (noticeEl) area.appendChild(noticeEl);
        const statusEl = createElement('p', { className: 'cid-wizard-text', textContent: 'Enter your PIN' });
        area.appendChild(statusEl);

        area.appendChild(createPinEntry({
            pinLength: pinLen,
            onComplete: async (pin) => {
                if (!firstPin) {
                    firstPin = pin;
                    noticeEl?.remove();
                    statusEl.textContent = 'Enter the same PIN again to confirm';
                    area.querySelector('.cid-pin-entry')?.remove();
                    area.appendChild(createPinEntry({
                        pinLength: pinLen,
                        onComplete: async (confirmPin) => {
                            if (confirmPin !== firstPin) {
                                renderPinChoice(area, pinLen, onChosen,
                                    'Those two PINs did not match. Choose your PIN again, then confirm it.');
                                return;
                            }
                            onChosen(confirmPin);
                        },
                    }));
                }
            },
        }));
    }
}

/**
 * Paint the "Keys on this device" section (§8).
 *
 * Thin by design: every decision — which rows exist, which are ticked, which
 * are locked, which are new — belongs to {@link createDeviceKeyPicker}, and
 * this function only turns those rows into checkboxes and reports clicks back.
 *
 * Folded into one summary line ("all 11", "9 of 11 — 2 left out"): eleven rows
 * open by default pushed the Configure button below the fold, and nearly
 * everybody keeps every key (staging test 2026-10-15). It opens by itself when
 * this device already leaves keys out, so a choice made earlier is never
 * hidden from the person who made it.
 *
 * The copy says what declining costs and no more: a key left out is still
 * usable here after a supersecret prompt (§1 item 12), which is the whole
 * bargain. Nothing here talks about per-device money isolation — with
 * `derive_root` mandatory the picker cannot deliver it, and saying otherwise
 * would be a promise the code does not keep (§1 item 16).
 */
function renderKeyPickerSection(picker: DeviceKeyPicker): HTMLElement {
    const section = createElement('div', { className: 'cid-device-keys-section' });
    section.style.margin = '16px 0';

    const details = document.createElement('details');
    const summary = document.createElement('summary');
    summary.className = 'cid-device-mode-label';
    summary.style.cursor = 'pointer';
    const count = createElement('span');
    count.style.fontWeight = '400';
    const hint = createElement('span', { className: 'cid-pane-note', textContent: ' · Choose keys…' });
    summary.append('Keys on this device: ', count, hint);
    details.appendChild(summary);
    section.appendChild(details);

    const update = (): void => {
        const rows = picker.rows();
        const kept = rows.filter(r => picker.isChosen(r.id)).length;
        const left = rows.length - kept;
        count.textContent = left === 0 ? `all ${rows.length}` : `${kept} of ${rows.length} — ${left} left out`;
        hint.style.display = details.open ? 'none' : '';
    };
    details.open = picker.rows().some(r => !picker.isChosen(r.id));
    details.addEventListener('toggle', update);
    details.addEventListener('change', update);

    details.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Choose which keys this device keeps. Keys you leave out can still be used '
            + 'here after entering your supersecret.',
    }));

    let currentGroup: string | null = null;
    for (const row of picker.rows()) {
        if (row.group !== currentGroup) {
            currentGroup = row.group;
            const groupEl = createElement('div', {
                className: 'cid-pane-note', textContent: currentGroup,
            });
            groupEl.style.marginTop = '12px';
            groupEl.style.marginBottom = '2px';
            groupEl.style.fontSize = '11px';
            groupEl.style.textTransform = 'uppercase';
            groupEl.style.letterSpacing = '0.05em';
            details.appendChild(groupEl);
        }
        details.appendChild(renderKeyPickerRow(picker, row));
    }
    update();
    return section;
}

/** One checkbox row. A `<label>`, so the whole line is the hit target. */
function renderKeyPickerRow(picker: DeviceKeyPicker, row: DeviceKeyPickerRow): HTMLElement {
    const el = document.createElement('label');
    el.className = 'cid-device-key-row';
    el.style.display = 'flex';
    el.style.alignItems = 'flex-start';
    el.style.gap = '8px';
    el.style.padding = '4px 0';
    if (!row.mandatory) el.style.cursor = 'pointer';

    const box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = row.chosen;
    box.value = row.id;
    box.style.marginTop = '3px';
    box.style.accentColor = 'cornflowerblue';
    // Mandatory rows are ticked and DISABLED: the three Core ids are constants,
    // not a member choice (§1 item 10, §12). The picker refuses the change too
    // — this only keeps the screen from suggesting one.
    if (row.mandatory) box.disabled = true;
    box.addEventListener('change', () => {
        picker.setChosen(row.id, box.checked);
        // Re-read rather than trust the box: an id the picker refuses to change
        // must not be left showing the member's click as if it took.
        box.checked = picker.isChosen(row.id);
    });
    el.appendChild(box);

    const textCol = createElement('div');
    const labelLine = createElement('div', { className: 'cid-device-mode-label' });
    labelLine.appendChild(document.createTextNode(row.label));
    if (row.mandatory) labelLine.appendChild(pickerTag('(required)', '#888'));
    if (row.isNew) labelLine.appendChild(pickerTag('new', 'cornflowerblue'));
    textCol.appendChild(labelLine);
    textCol.appendChild(createElement('div', {
        className: 'cid-device-mode-desc', textContent: row.description,
    }));
    el.appendChild(textCol);
    return el;
}

/** The small "(required)" / "new" marker beside a row's label. */
function pickerTag(text: string, color: string): HTMLElement {
    const tag = createElement('span', { textContent: text });
    tag.style.marginLeft = '6px';
    tag.style.fontSize = '11px';
    tag.style.fontWeight = '400';
    tag.style.color = color;
    return tag;
}

/** Everything one device write needs, settled by the screens above. */
interface DeviceSetupSubmission {
    /** The node's own vocabulary — `pin` or `open`. The Authenticator factor is
     *  orthogonal and rides in `totpRequired` (§3). */
    accessMode: 'pin' | 'open';
    pin?: string;
    pinLength?: 4 | 6 | 8;
    totpRequired: boolean;
    code?: string;
    label: string;
    /** Which keys this device keeps (§8) — the ONE field here that never
     *  reaches the node. `doSetup` builds the request field by field for that
     *  reason: nothing spreads this object onto the wire. */
    manifest: DeviceKeyManifest;
}

/** Encrypt keys to localStorage and call the onSetup callback for the API call.
 *
 *  `onFailure` lets a caller that can meaningfully retry — the code step, whose
 *  refusal is usually a thirty-second-old code — take the error back instead of
 *  ending on a dead-end status line.
 *
 *  Exported for its tests; `renderDeviceSetup` is the only production caller. */
export async function doSetup(
    container: HTMLElement,
    bag: KeyBag,
    cid: string,
    submission: DeviceSetupSubmission,
    callbacks: DeviceSetupCallbacks,
    onFailure?: (message: string, err: unknown) => void,
): Promise<void> {
    container.innerHTML = '';
    const statusEl = createElement('p', { className: 'cid-wizard-text', textContent: 'Setting up device...' });
    container.appendChild(statusEl);

    try {
        const deviceToken = deviceStorage.getDeviceToken(cid) ?? generateDeviceToken();
        const dek = aes.generateSymmetricKey();

        // Which keys this device keeps (device-setup-restructure §7, §8) — the
        // member's own selection, straight off the picker. Written BEFORE the
        // copy so the manifest and the keys on disk always agree, even if the
        // node refuses the row a moment later: a device holding fewer keys than
        // its manifest claims would be reported stale on every unlock.
        const manifest = submission.manifest;
        writeDeviceKeyManifest(cid, manifest);

        await storeBagOnDevice(cid, bag, dek, manifest);

        // Make the API call first — only persist token after success
        // to avoid stale tokens on failure. Every field is named here rather
        // than spread: the manifest sits beside them in `submission` and must
        // never reach the node (§1 item 9).
        await callbacks.onSetup({
            deviceToken,
            deviceEncryptionKey: aes.toBase64(dek),
            accessMode: submission.accessMode,
            pin: submission.pin,
            pinLength: submission.pinLength,
            totpRequired: submission.totpRequired,
            code: submission.code,
            label: submission.label,
        }, manifest);

        deviceStorage.setDeviceToken(cid, deviceToken);
        if (submission.accessMode === 'pin' && submission.pinLength) {
            deviceStorage.setPinLength(cid, submission.pinLength);
        }
        // What the unlock screen paints on its first frame (§6.1). Written only
        // after the node accepted the row, so the hint can never describe a
        // mode the node refused — and cleared outright for a mode that asks for
        // nothing, so a stale "pin" never paints a pad for an open device.
        rememberUnlockFactors(cid, submission.accessMode, submission.totpRequired);

        statusEl.textContent = 'Device configured';
    } catch (err) {
        const message = err instanceof Error ? err.message : 'Device setup failed';
        if (onFailure) {
            onFailure(message, err);
            return;
        }
        statusEl.textContent = message;
        statusEl.className = 'cid-pane-error cid-pane-error--visible';
    }
}

/**
 * Write a decrypted bag into THIS device's storage under the device encryption
 * key, preserving each entry's keyIndex, algorithm and status (chain-walking
 * needs all three). Returns the key ids actually written.
 *
 * TWO filters, and they are different in kind:
 *  - {@link isDeviceStorableKeyId} — the Recovery Kit entry never reaches ANY
 *    device, because its coefficients plus a single Recovery Sheet reconstruct
 *    the supersecret (registration-restructure §16.5). Not a choice.
 *  - `manifest` — what the MEMBER chose for THIS device
 *    (device-setup-restructure §7.3, whitepaper §3.4.7). Declined ids are
 *    skipped; the three mandatory ids are written whatever it says. Pass null
 *    (or omit) and this device's stored manifest is read; an absent one means
 *    everything is chosen.
 *
 * This is the widest bag-to-device path in the client — it runs after a
 * supersecret unlock AND after a bundle restore, both of which have the whole
 * bag in hand — so both filters belong here rather than at each caller.
 *
 * `store` is injected for tests only; production always uses the real one.
 */
export async function storeBagOnDevice(
    cid: string,
    bag: KeyBag,
    deviceEncryptionKey: Uint8Array,
    manifest: DeviceKeyManifest | null = null,
    store: (
        cid: string, keyId: string, privateKey: Uint8Array, dek: Uint8Array,
        meta: { keyIndex: number; algorithm: string; status: string },
    ) => Promise<void> = deviceStorage.encryptAndStoreKey,
): Promise<string[]> {
    const selection = manifest ?? readDeviceKeyManifest(cid);
    const stored: string[] = [];
    for (const entry of bag.entries as KeyBagEntry[]) {
        if (!isDeviceStorableKeyId(entry.keyId)) continue;
        if (!isDeviceKeyChosen(selection, entry.keyId)) continue;
        await store(cid, entry.keyId, aes.fromBase64(entry.privateKey), deviceEncryptionKey, {
            keyIndex: entry.keyIndex ?? 0,
            algorithm: entry.algorithm,
            status: entry.status ?? 'active',
        });
        stored.push(entry.keyId);
    }
    return stored;
}
